/**
 * UserPreferencesContext — centrale opslag & sync van gebruikersvoorkeuren.
 *
 * Wraps de Enterprise Workspace zodat:
 *   - Favorieten (menu / widget / command / entity)
 *   - Recent bezocht
 *   - Dashboard layout & widget instellingen
 *   - Navigatie voorkeuren (sidebar-collapsed, expanded groups)
 * op ÉÉN plek beheerd worden, backend-synchroon, per user + per tenant.
 *
 * Backend: /api/admin/user-preferences (GET, PUT), .../favorites/toggle,
 *   .../recent-visits/add.
 *
 * Consumers gebruiken:
 *   const {
 *     prefs, loading,
 *     toggleFavorite, isFavorite,
 *     toggleWidgetFavorite, isWidgetFavorite,     // dashboard-widget favs
 *     toggleCommandFavorite, isCommandFavorite,   // command-palette action favs
 *     recordVisit, patchPrefs,
 *   } = useUserPreferences();
 *
 * BELANGRIJK: `recordVisit` is een DEBOUNCED write met een korte timer
 * (1000 ms) zodat snelle module-switches niet 20 requests genereren.
 *
 * FAVORITES key-shape (Sprint Fase A Afronding · Feb 2026):
 *   - Menu-items: `"<nav_key>"` (bv "facturen")
 *   - Widgets:    `"widget:<widget_key>"` (bv "widget:tracking-updater")
 *   - Commands:   `"cmd:<action_key>"` (bv "cmd:new-invoice")
 *   - Entities:   `"entity:<type>:<id>"` (bv "entity:customer:abc123")
 * Alle types delen dezelfde MAX_FAVORITES=8-limiet niet — ze worden per
 * namespace geteld door de helpers hieronder.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const DEFAULT_PREFS = {
  favorites: [],
  recent_visits: [],
  dashboard_layout: {},
  widget_settings: {},
  navigation_prefs: {
    sidebar_collapsed: false,
    expanded_groups: [],
  },
};

const UserPreferencesCtx = createContext({
  prefs: DEFAULT_PREFS,
  loading: true,
  toggleFavorite: async () => {},
  isFavorite: () => false,
  toggleWidgetFavorite: async () => {},
  isWidgetFavorite: () => false,
  toggleCommandFavorite: async () => {},
  isCommandFavorite: () => false,
  favoriteWidgets: () => [],
  favoriteCommands: () => [],
  recordVisit: () => {},
  patchPrefs: async () => {},
  reload: async () => {},
});

export function UserPreferencesProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/user-preferences");
      setPrefs({ ...DEFAULT_PREFS, ...(r.data || {}) });
    } catch (e) {
      console.warn("UserPreferences load failed:", formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      reload();
    } else {
      setPrefs(DEFAULT_PREFS);
      setLoading(false);
    }
  }, [user, reload]);

  // ─── Generic favorite toggle ─────────────────────────────
  const _toggleFavKey = useCallback(async (key, notify = true) => {
    if (!key) return;
    try {
      const r = await adminApi.post("/admin/user-preferences/favorites/toggle", { key });
      setPrefs((p) => ({ ...p, favorites: r.data?.favorites || [] }));
      if (notify) {
        if (r.data?.action === "added") toast.success("Toegevoegd aan favorieten");
        else toast("Verwijderd uit favorieten");
      }
      return r.data;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const toggleFavorite = useCallback(async (key) => _toggleFavKey(key, true), [_toggleFavKey]);
  const isFavorite = useCallback(
    (key) => (prefs.favorites || []).includes(key),
    [prefs.favorites],
  );

  // ─── Widget favorites (dashboard) ─────────────────────────
  const toggleWidgetFavorite = useCallback(
    (widgetKey) => _toggleFavKey(`widget:${widgetKey}`, true),
    [_toggleFavKey],
  );
  const isWidgetFavorite = useCallback(
    (widgetKey) => (prefs.favorites || []).includes(`widget:${widgetKey}`),
    [prefs.favorites],
  );
  const favoriteWidgets = useCallback(
    () => (prefs.favorites || [])
      .filter((k) => typeof k === "string" && k.startsWith("widget:"))
      .map((k) => k.substring("widget:".length)),
    [prefs.favorites],
  );

  // ─── Command favorites (Command Palette actions) ─────────
  const toggleCommandFavorite = useCallback(
    (cmdKey) => _toggleFavKey(`cmd:${cmdKey}`, true),
    [_toggleFavKey],
  );
  const isCommandFavorite = useCallback(
    (cmdKey) => (prefs.favorites || []).includes(`cmd:${cmdKey}`),
    [prefs.favorites],
  );
  const favoriteCommands = useCallback(
    () => (prefs.favorites || [])
      .filter((k) => typeof k === "string" && k.startsWith("cmd:"))
      .map((k) => k.substring("cmd:".length)),
    [prefs.favorites],
  );

  // ─── Debounced record-visit — max 1 call per seconde per key ─
  const recordVisit = useCallback((visit) => {
    if (!visit || !visit.key) return;
    setPrefs((p) => {
      const list = (p.recent_visits || []).filter((v) => !(v.key === visit.key && v.entity_id === visit.entity_id));
      list.unshift({ ...visit, visited_at: new Date().toISOString() });
      return { ...p, recent_visits: list.slice(0, 10) };
    });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await adminApi.post("/admin/user-preferences/recent-visits/add", { visit });
      } catch { /* silent */ }
    }, 1000);
  }, []);

  const patchPrefs = useCallback(async (partial) => {
    try {
      const r = await adminApi.put("/admin/user-preferences", partial);
      setPrefs({ ...DEFAULT_PREFS, ...(r.data || {}) });
      return r.data;
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const value = useMemo(() => ({
    prefs, loading,
    toggleFavorite, isFavorite,
    toggleWidgetFavorite, isWidgetFavorite, favoriteWidgets,
    toggleCommandFavorite, isCommandFavorite, favoriteCommands,
    recordVisit, patchPrefs, reload,
  }), [
    prefs, loading, toggleFavorite, isFavorite,
    toggleWidgetFavorite, isWidgetFavorite, favoriteWidgets,
    toggleCommandFavorite, isCommandFavorite, favoriteCommands,
    recordVisit, patchPrefs, reload,
  ]);

  return (
    <UserPreferencesCtx.Provider value={value}>
      {children}
    </UserPreferencesCtx.Provider>
  );
}

export function useUserPreferences() {
  return useContext(UserPreferencesCtx);
}
